import React, { useMemo } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { GridRow, getLeadRow } from '../../utils/phone';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { LeadDetails } from 'libs/feature-backoffice-users/src/backOfficeUsers/components/leadDetails/leadDetails';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import {
  BORROWER_OBJECT_TYPE,
  BROKER_OBJECT_TYPE,
  LEAD_OBJECT_TYPE,
  TPO_OBJECT_TYPE,
} from 'libs/feature-backoffice-users/src/backOfficeUsers/components/leads/constants/constants';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { BorrowerDetailedScreen } from 'libs/feature-backoffice-users/src/backOfficeUsers/components/borrowerDetails/borrowerDetailedScreen';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { LenderDetailedScreen } from 'libs/feature-backoffice-users/src/backOfficeUsers/components/lenderDetails/lenderDetailedScreen';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      flex: 1,
      padding: '20px',
      overflowY: 'auto',
      height: '100%',
      maxHeight: '80vh',
    },
    placeholder: {
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#666',
      fontSize: '1.1rem',
    },
  })
);

export type LeadDetailsPanelProps = {
  salesforceLeadStore: any;
  selectedLeadId: string | null;
};

const LeadDetailsPanel: React.FC<LeadDetailsPanelProps> = ({
  salesforceLeadStore,
  selectedLeadId,
}) => {
  const classes = useStyles();

  const dashboardType =
    salesforceLeadStore?.leadsDashboardStore?.currentDashboard?.objectType?.toUpperCase() ??
    salesforceLeadStore?.leadsDashboardStore?.currentDashboard?.baseObject?.toUpperCase() ??
    LEAD_OBJECT_TYPE;

  const gridRows: GridRow[] =
    salesforceLeadStore?.leadsDashboardStore?.leadsDashboardGridStore
      ?.gridData?.data?.rows ?? [];

  const selectedRow = useMemo(
    () => getLeadRow(gridRows, selectedLeadId),
    [gridRows, selectedLeadId]
  );

  const renderContent = () => {
    if (!selectedLeadId) {
      return (
        <Box className={classes.placeholder}>
          Select a record to see details
        </Box>
      );
    }

    const recordTypeFromRow = (selectedRow?.recordType as string | undefined)?.toUpperCase();

    const effectiveType =
      dashboardType === LEAD_OBJECT_TYPE
        ? LEAD_OBJECT_TYPE
        : recordTypeFromRow || dashboardType;

    switch (effectiveType) {
      case BORROWER_OBJECT_TYPE: {
        const entityId =
          selectedRow?.borrowerId ??
          selectedRow?.entityId ??
          selectedLeadId;

        return (
          <BorrowerDetailedScreen
            key={String(entityId)}
            id={entityId}
            fromCadence
          />
        );
      }
      case TPO_OBJECT_TYPE:
      case BROKER_OBJECT_TYPE: {
        const entityId =
          selectedRow?.companyContactId ??
          selectedRow?.entityId ??
          selectedLeadId;

        const recordType = selectedRow?.recordType ?? dashboardType;

        return (
          <LenderDetailedScreen
            key={String(entityId)}
            id={entityId}
            fromCadence
            contactRecordType={recordType}
          />
        );
      }
      default:
        return (
          <LeadDetails
            key={String(selectedLeadId)}
            salesforceLeadStore={salesforceLeadStore}
            id={Number(selectedLeadId)}
            fromCadence={false}
            noteDialogContext="inline"
          />
        );
    }
  };

  return (
    <Box className={classes.container}>
      {renderContent()}
    </Box>
  );
};

export default LeadDetailsPanel;
